// Mirrors the API's InventoryStatus enum (serialized by name).
export type InventoryStatus = 'Active' | 'Picked' | 'Relocated' | 'Removed' | 'Assigned' | 'Damaged';

// Statuses an admin may bulk-set by hand. Assigned is excluded: it is only reached by adding an
// item to an order, and the API refuses it as a bulk target.
export const BULK_SETTABLE_STATUSES = ['Active', 'Picked', 'Removed', 'Damaged'] as const satisfies readonly InventoryStatus[];
export type BulkSettableStatus = (typeof BULK_SETTABLE_STATUSES)[number];

export function isBulkSettableStatus(value: string): value is BulkSettableStatus {
  return (BULK_SETTABLE_STATUSES as readonly string[]).includes(value);
}

export interface BulkSetStatusResult {
  updatedCount: number;
  /** Coils left unchanged because they are on an order; the message names them. */
  skippedCount: number;
}

export interface InventoryItem {
  id: number;
  barcodeValue: string;
  manifestId: number;
  manifestBargeName: string;
  locationId?: number;
  locationCode?: string;
  sortId1?: string;
  sortId2?: string;
  receivedTimestamp: string;
  receivedSequence: number;
  locationAssignedAt?: string;
  isFlagged: boolean;
  status: InventoryStatus;
  createdAt: string;
  orders?: InventoryOrderInfo[];
}

export interface InventoryOrderInfo {
  orderId: number;
  orderNumber: string | null;
  orderStatus: string;
}

export interface Location {
  id: number;
  code: string;
  description: string;
  isActive: boolean;
  itemCount: number;
  createdAt: string;
}

export interface CreateLocationRequest {
  code: string;
  description: string;
  isActive?: boolean;
}

export interface UpdateLocationRequest {
  code?: string;
  description?: string;
  isActive?: boolean;
}

export interface SortId {
  id: number;
  code: string;
  description: string;
  type: 1 | 2;
  isActive: boolean;
  createdAt: string;
}

export interface CreateSortIdRequest {
  code: string;
  description: string;
  type: 1 | 2;
  isActive?: boolean;
}

export interface UpdateSortIdRequest {
  code?: string;
  description?: string;
  isActive?: boolean;
}

export interface InventoryQueryParams {
  /** Partial, case-insensitive, trimmed barcode match. For free-text barcode search. */
  barcodeSearch?: string;
  locationId?: number;
  sortId1?: string;
  sortId2?: string;
  status?: InventoryStatus;
  needsHome?: boolean;
  manifestName?: string;
  manifestId?: number;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface PagedInventoryResponse {
  items: InventoryItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface InventoryMovement {
  id: number;
  inventoryItemId: number;
  barcodeValue: string;
  fromLocationId?: number;
  fromLocationCode?: string;
  toLocationId?: number;
  toLocationCode?: string;
  movedAt: string;
  movedByName: string;
  notes?: string;
}
